import { useState, useMemo } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Plane,
  Users,
  Boxes,
  ClipboardList,
  CalendarClock,
  DollarSign,
  Settings,
  Bell,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Menu,
  GanttChart,
  PlaneTakeoff,
  Building2,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { getInitials, Role } from '@avgrp/shared';
import { hasMinRole } from '@/utils/roles';

// ─── Navigation items ───────────────────────────────────────

interface NavItem {
  label: string;
  path: string;
  icon: React.ElementType;
  minRole?: Role; // minimum role required to see this item
}

interface NavGroup {
  category: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    category: 'Operations',
    items: [
      { label: 'Dashboard', path: '/', icon: LayoutDashboard },
      { label: 'Flights', path: '/flights', icon: Plane },
      { label: 'Gantt', path: '/gantt', icon: GanttChart },
    ],
  },
  {
    category: 'Resources',
    items: [
      { label: 'Fleet', path: '/fleet', icon: Plane, minRole: Role.SUPERVISOR },
      { label: 'Aircraft Types', path: '/aircraft-types', icon: PlaneTakeoff, minRole: Role.SUPERVISOR },
      { label: 'Airport', path: '/airport', icon: Building2, minRole: Role.SUPERVISOR },
      { label: 'Resources', path: '/resources', icon: Boxes, minRole: Role.SUPERVISOR },
      { label: 'Users', path: '/users', icon: Users, minRole: Role.ADMIN },
    ],
  },
  {
    category: 'Planning',
    items: [
      { label: 'Tasks', path: '/tasks', icon: ClipboardList },
      { label: 'Scheduling', path: '/scheduling', icon: CalendarClock },
    ],
  },
  {
    category: 'Administration',
    items: [
      { label: 'Costs', path: '/costs', icon: DollarSign, minRole: Role.MANAGER },
      { label: 'Settings', path: '/settings', icon: Settings, minRole: Role.ADMIN },
    ],
  },
];

// ─── Layout Component ───────────────────────────────────────

export function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const initials = user ? getInitials(user.firstName, user.lastName) : '??';

  // Filter nav groups by user role, removing empty groups
  const visibleGroups = useMemo(() => {
    return navGroups
      .map((group) => ({
        ...group,
        items: group.items.filter(
          (item) => !item.minRole || hasMinRole(user?.role, item.minRole),
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [user?.role]);

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Sidebar */}
      <aside
        className={`
          flex flex-col border-r border-gray-200 bg-aviation-950 text-white
          transition-all duration-300 ease-in-out
          ${collapsed ? 'w-16' : 'w-64'}
        `}
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-between border-b border-aviation-800 px-4">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <Plane className="h-6 w-6 text-aviation-400" />
              <span className="text-lg font-bold tracking-tight">AVGRP</span>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-aviation-900 hover:text-white"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-2 py-4 scrollbar-thin">
          {visibleGroups.map((group, groupIndex) => (
            <div key={group.category} className={groupIndex > 0 ? 'mt-4' : ''}>
              {/* Category header — visible only when expanded */}
              {!collapsed && (
                <h4 className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                  {group.category}
                </h4>
              )}

              {/* Thin separator when collapsed (skip first group) */}
              {collapsed && groupIndex > 0 && (
                <div className="mx-2 mb-2 border-t border-aviation-800" />
              )}

              <div className="space-y-1">
                {group.items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === '/'}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors
                      ${
                        isActive
                          ? 'bg-aviation-700 text-white'
                          : 'text-gray-300 hover:bg-aviation-900 hover:text-white'
                      }
                      ${collapsed ? 'justify-center' : ''}
                      `
                    }
                    title={collapsed ? item.label : undefined}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    {!collapsed && <span>{item.label}</span>}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* User section at bottom */}
        <div className="border-t border-aviation-800 p-3">
          <div
            className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`}
          >
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-aviation-600 text-xs font-semibold">
              {initials}
            </div>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="truncate text-xs text-gray-400">{user?.role}</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top header */}
        <header className="flex h-16 items-center justify-between border-b border-gray-200 bg-white px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 lg:hidden"
              aria-label="Toggle navigation"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>

          <div className="flex items-center gap-3">
            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                className="relative rounded-lg p-2 text-gray-500 hover:bg-gray-100"
                aria-label="View notifications"
              >
                <Bell className="h-5 w-5" />
                <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-red-500" />
              </button>

              {notificationsOpen && (
                <div className="absolute right-0 top-full z-50 mt-2 w-80 rounded-xl border border-gray-200 bg-white shadow-lg">
                  <div className="border-b border-gray-100 px-4 py-3">
                    <h3 className="text-sm font-semibold text-gray-900">
                      Notifications
                    </h3>
                  </div>
                  <div className="max-h-96 overflow-y-auto p-2">
                    <p className="px-3 py-8 text-center text-sm text-gray-400">
                      No new notifications
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6 scrollbar-thin">
          <Outlet />
        </main>
      </div>

      {/* Click-away overlay for notifications */}
      {notificationsOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setNotificationsOpen(false)}
        />
      )}
    </div>
  );
}
